'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Hotspot {
  id: number;
  x: number;
  y: number;
  title: string;
  specs: string[];
  description: string;
}

const hotspots: Hotspot[] = [
{
  id: 1,
  x: 25,
  y: 30,
  title: 'Laser Head Assembly',
  description: 'High-precision fiber laser cutting head with auto-focus and collision detection.',
  specs: [
  '15kW Fiber Laser Source',
  'Auto-Focus Technology',
  'Capacitive Height Control',
  'Collision Detection System',
  'Nitrogen/Oxygen Assist Gas']

},
{
  id: 2,
  x: 50,
  y: 55,
  title: 'Cutting Bed',
  description: 'Expansive work area with automated material handling and precision positioning.',
  specs: [
  '6000mm x 2000mm Work Area',
  'Automated Shuttle Table',
  'Precision Ball Screw Drives',
  'Servo Motor Control',
  'Rapid Positioning: 120m/min']

},
{
  id: 3,
  x: 75,
  y: 35,
  title: 'Control Console',
  description: 'Advanced CNC control system with offline programming and real-time monitoring.',
  specs: [
  'Siemens CNC Control',
  'Offline CAM Programming',
  'Real-Time Process Monitoring',
  'Automatic Nesting Software',
  'Remote Diagnostics']

}];


export default function InteractiveFiberLaser() {
  const [activeHotspot, setActiveHotspot] = useState<number | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  const handleHotspotClick = (id: number) => {
    if (!isHydrated) return;
    setActiveHotspot(activeHotspot === id ? null : id);
  };

  return (
    <section className="lg:py-24 bg-gradient-to-b from-background to-muted/30 py-[70px]">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full mb-4">
            <Icon name="CursorArrowRaysIcon" size={20} variant="solid" className="text-accent" />
            <span className="text-sm font-cta font-semibold text-accent">Interactive Showcase</span>
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-foreground mb-6">
            Explore Our Fiber Laser System
          </h2>
          
          <p className="text-lg text-muted-foreground leading-relaxed">
            Click on the hotspots to discover the advanced technology behind our precision cutting capabilities.
          </p>
        </div>
        
        <div className="max-w-6xl mx-auto">
          <div className="relative bg-card rounded-xl shadow-lg overflow-hidden border border-border">
            <div className="relative aspect-video">
              <AppImage
                src="/assets/images/Sheet_Metal_Laser-1770002295982.jpg"
                alt="Industrial fiber laser cutting machine with blue laser beam cutting through metal sheet, showing precision manufacturing equipment in modern factory setting"
                className="w-full h-full object-cover" />

              
              {hotspots.map((hotspot) =>
              <button
                key={hotspot.id}
                onClick={() => handleHotspotClick(hotspot.id)}
                className="absolute w-12 h-12 -ml-6 -mt-6 group"
                style={{ left: `${hotspot.x}%`, top: `${hotspot.y}%` }}
                aria-label={`View details about ${hotspot.title}`}>

                  <div className="relative w-full h-full">
                    <div className="absolute inset-0 bg-accent rounded-full animate-ping opacity-75"></div>
                    <div className="relative w-full h-full bg-accent rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-base">
                      <Icon name="PlusIcon" size={24} variant="outline" className="text-white" />
                    </div>
                  </div>
                </button>
              )}
            </div>
            
            {isHydrated && activeHotspot !== null &&
            <div className="absolute bottom-0 left-0 right-0 bg-card/95 backdrop-blur-sm border-t border-border p-6 lg:p-8 animate-in slide-in-from-bottom duration-300">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl lg:text-2xl font-headline font-bold text-foreground mb-2">
                      {hotspots.find((h) => h.id === activeHotspot)?.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {hotspots.find((h) => h.id === activeHotspot)?.description}
                    </p>
                  </div>
                  <button
                  onClick={() => setActiveHotspot(null)}
                  className="p-2 hover:bg-muted rounded-md transition-colors duration-base"
                  aria-label="Close details">

                    <Icon name="XMarkIcon" size={24} variant="outline" className="text-muted-foreground" />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {hotspots.find((h) => h.id === activeHotspot)?.specs.map((spec, index) =>
                <div key={index} className="flex items-center space-x-2 bg-muted/50 rounded-md px-3 py-2">
                      <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success flex-shrink-0" />
                      <span className="text-sm text-foreground">{spec}</span>
                    </div>
                )}
                </div>
              </div>
            }
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground flex items-center justify-center space-x-2">
              <Icon name="CursorArrowRippleIcon" size={20} variant="outline" />
              <span>Click the hotspots to explore technical specifications</span>
            </p>
          </div>
        </div>
      </div>
    </section>);

}